import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api';
import { useResource } from './use-resource';
import type { User } from '../mock/seeds/schema-types';

// Holds the authenticated admin (GET /v1/me) for the whole surface. The admin JWT
// carries the section grants (API-13); the shell reads them from here to label the
// account block and gate nav sections. Unlike the merchant surface there is no active
// business — the console is platform-wide. Duplicated per surface (CB-10).
interface SessionContextValue {
  user: User | undefined;
  grants: string[];
  loading: boolean;
  error: unknown;
  reload: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, loading, error, reload } = useResource<User>((signal) => api.GET('/me', { signal }), []);
  const value = useMemo<SessionContextValue>(
    () => ({ user: data, grants: (data?.grants as string[] | undefined) ?? [], loading, error, reload }),
    [data, loading, error, reload],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used within a SessionProvider');
  return ctx;
}
